
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type { Candidate } from "../../types";

interface UseHiredCandidateFormProps {
  candidate: Candidate;
  onSave: (updatedCandidate: Candidate) => void;
  onClose: () => void;
}

export const useHiredCandidateForm = ({ candidate, onSave, onClose }: UseHiredCandidateFormProps) => {
  const [startDate, setStartDate] = useState<Date | undefined>(
    candidate.start_date ? new Date(candidate.start_date) : undefined
  );
  const [contractLength, setContractLength] = useState(candidate.contract_length || "");
  const [placementFee, setPlacementFee] = useState(candidate.placement_fee?.toString() || "");
  const [acceptedDate, setAcceptedDate] = useState<Date | undefined>(
    candidate.accepted_date ? new Date(candidate.accepted_date) : new Date()
  );
  const [guaranteeEndDate, setGuaranteeEndDate] = useState<Date | undefined>(
    candidate.guarantee_end_date ? new Date(candidate.guarantee_end_date) : undefined
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      // Determine which field to use in the database (company_rate or placement_fee)
      // For now, using placement_fee since we've updated the Candidate type to use this field
      const { data, error } = await supabase
        .from("candidates")
        .update({
          start_date: startDate?.toISOString(),
          contract_length: contractLength,
          company_rate: parseFloat(placementFee) || null, // Map to company_rate in the database
          accepted_date: acceptedDate?.toISOString(),
          guarantee_end_date: guaranteeEndDate?.toISOString(),
          status: "hired"
        })
        .eq("id", candidate.id)
        .select()
        .single();

      if (error) throw error;

      if (data) {
        // Need to handle this properly - data could be missing the fields we need
        // Create an updated candidate with all required fields
        const updatedCandidate: Candidate = {
          ...candidate, // Keep all existing candidate data
          // Override with the values from the form
          placement_fee: parseFloat(placementFee) || null, // Use the form value directly
          start_date: startDate?.toISOString() || null,
          contract_length: contractLength || null,
          accepted_date: acceptedDate?.toISOString() || null,
          guarantee_end_date: guaranteeEndDate?.toISOString() || null,
          status: "hired"
        };
        
        onSave(updatedCandidate);
        toast.success("Hiring details saved successfully");
        onClose();
      }
    } catch (error) {
      console.error("Error saving hiring details:", error);
      toast.error("Failed to save hiring details");
    }
  };

  return {
    startDate,
    setStartDate,
    contractLength,
    setContractLength,
    placementFee,
    setPlacementFee,
    acceptedDate,
    setAcceptedDate,
    guaranteeEndDate,
    setGuaranteeEndDate,
    handleSubmit,
  };
};
